import { useCallback, useEffect, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import "./HPDashboard.scss";
import {
  DeviceFillIcon,
  PersonIcon,
} from "../../../assets/icons-components/Icons";
import { GetHealthProfessionalDashboardData } from "../../../utils/apiCalls/dashboard";
import { GetAllPatientDetailsHealthProfessional } from "../../../utils/apiCalls/patients";
import DashboardCard from "../../../components/DashboardCard/DashboardCard";
import Buttons from "../../../components/Buttons/Buttons";
import UserAvatar from "../../../components/UserAvatar/UserAvatar";
import { UserContext } from "../../../store/UserContext";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import WatchIcon from "@mui/icons-material/Watch";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import Skeleton from "@mui/material/Skeleton";

// Never render undefined/null while data is loading or a count is missing.
const validateValue = (value) => (value >= 0 ? value : 0);

const HPDashboard = () => {
  const navigate = useNavigate();
  const { loginUserDetails } = useContext(UserContext);
  const user = loginUserDetails?.currentHealthProfessionalModel || {};

  const [dashboardData, setDashboardData] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [fetchError, setFetchError] = useState("");

  const [recentPatients, setRecentPatients] = useState([]);
  const [isPatientsLoading, setIsPatientsLoading] = useState(true);

  const fetchHealthProfessionalDashboardData = useCallback(async () => {
    setIsLoading(true);
    setFetchError("");
    try {
      const res = await GetHealthProfessionalDashboardData();
      const { data, message, statusCode } = res;
      if (statusCode === 200) {
        setDashboardData(data || {});
      } else {
        setFetchError(message || "Failed to load dashboard data");
      }
    } catch (error) {
      setFetchError(error?.message || "Failed to load dashboard data");
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchRecentPatients = useCallback(async () => {
    setIsPatientsLoading(true);
    try {
      const payload = {
        pageIndex: 0,
        pageSize: 5,
        searchString: "",
        selectedDate: null,
      };
      const res = await GetAllPatientDetailsHealthProfessional(payload);
      if (res?.statusCode === 200 && Array.isArray(res?.data)) {
        setRecentPatients(res.data.slice(0, 5));
      } else {
        setRecentPatients([]);
      }
    } catch (err) {
      setRecentPatients([]);
    } finally {
      setIsPatientsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchHealthProfessionalDashboardData();
    fetchRecentPatients();
  }, [fetchHealthProfessionalDashboardData, fetchRecentPatients]);

  const handlePatientClick = (patient) => {
    const patientName = patient.patientName || patient.name || "Unnamed Patient";
    const rowData = {
      id: patient.patientId || patient.id,
      userId: patient.patientId || patient.userId || patient.id,
      name: patientName,
      patientName: patientName,
      gender: patient.gender,
      mobileNumber: patient.mobileNumber,
      workplace: patient.workPlace,
      deviceId: patient.deviceId,
      ...patient,
    };
    sessionStorage.setItem("rowData", JSON.stringify(rowData));
    sessionStorage.setItem("patientName", patientName);
    if (rowData.deviceId) {
      sessionStorage.setItem("deviceId", rowData.deviceId);
    }
    navigate("/patient-view", {
      state: { rowData, patientName },
    });
  };

  const assigned = validateValue(dashboardData.numberOfAssignedDevice);
  const totalDev = validateValue(dashboardData.numberOfDeviceInInventory);
  const available = validateValue(dashboardData.numberOfUnAssignedDevice);

  return (
    <div className="hp-dashboard">
      {/* CLEAN PAGE HEADER */}
      <div className="hp-dashboard_header">
        <div className="hp-dashboard_header_info">
          <h1>Welcome back, {user.name ? user.name : "Healthcare Professional"}</h1>
          <p>
            Overview of clinical patients, device inventory, and active workspace logs.
          </p>
        </div>
        <div className="hp-dashboard_header_actions">
          <Buttons
            text="Add Patient"
            type="button"
            variant="primary"
            fullWidth={false}
            style={{ height: 38 }}
            icon={<PersonAddIcon fontSize="small" />}
            onClick={() => navigate("/patient-form")}
          />
          <Buttons
            text="My Devices"
            type="button"
            variant="secondary"
            fullWidth={false}
            style={{ height: 38 }}
            icon={<WatchIcon fontSize="small" />}
            onClick={() => navigate("/my-devices")}
          />
        </div>
      </div>

      {fetchError && (
        <div className="hp-dashboard_error">
          <span>{fetchError}</span>
          <Buttons
            text="Retry"
            type="button"
            variant="smallsecondary"
            onClick={fetchHealthProfessionalDashboardData}
          />
        </div>
      )}

      {/* KPI METRIC CARDS */}
      <div className="hp-dashboard_cards">
        <DashboardCard
          icon={<PersonIcon />}
          isLoading={isLoading}
          value={`${validateValue(dashboardData.numberOfTotalUsers)} / ${validateValue(dashboardData.numberOfUsers)}`}
          label="Total Number of Patients"
          onClick={() => navigate("/patient-table")}
        />
        <DashboardCard
          icon={<DeviceFillIcon />}
          isLoading={isLoading}
          value={`${assigned} / ${totalDev}`}
          label="Assigned Devices (of Inventory)"
          onClick={() => navigate("/my-devices")}
        />
        <DashboardCard
          icon={<DeviceFillIcon />}
          isLoading={isLoading}
          value={`${available} / ${totalDev}`}
          label="Available Devices (of Inventory)"
          onClick={() => navigate("/my-devices")}
        />
      </div>

      {/* CONTENT GRID */}
      <div className="hp-dashboard_grid">
        {/* RECENT PATIENTS */}
        <div className="hp-dashboard_grid_main">
          <div className="hp-card">
            <div className="hp-card_header">
              <div className="hp-card_header_title">
                <PersonIcon className="hp-card_icon" />
                <div>
                  <h3>Recent Patients</h3>
                  <p>Latest registered clinical patient records</p>
                </div>
              </div>
              <button
                className="hp-card_header_link"
                onClick={() => navigate("/patient-table")}
              >
                View All Patients <ArrowForwardIcon fontSize="small" />
              </button>
            </div>

            <div className="hp-card_body">
              {isPatientsLoading ? (
                <div className="hp-skeleton-list">
                  <Skeleton height={50} />
                  <Skeleton height={50} />
                  <Skeleton height={50} />
                </div>
              ) : recentPatients.length > 0 ? (
                <div className="hp-patient-list">
                  {recentPatients.map((patient) => (
                    <div
                      key={patient.patientId || patient.id}
                      className="hp-patient-item"
                      onClick={() => handlePatientClick(patient)}
                      title="Click to view patient details"
                    >
                      <UserAvatar
                        src={patient.imageUrl}
                        name={patient.patientName || patient.name}
                        sx={{ width: 40, height: 40, fontSize: 15 }}
                      />
                      <div className="hp-patient-item_info">
                        <h4>{patient.patientName || patient.name || "Unnamed Patient"}</h4>
                        <p>
                          {patient.gender ? `${patient.gender} • ` : ""}
                          {patient.mobileNumber || patient.workPlace || "Patient Record"}
                        </p>
                      </div>
                      <span className="hp-view-detail-hint">
                        View Details <ChevronRightIcon className="hp-patient-item_chevron" />
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="hp-empty-state">
                  <p>No patient records found.</p>
                  <Buttons
                    text="Add First Patient"
                    type="button"
                    variant="smallprimary"
                    onClick={() => navigate("/patient-form")}
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* DEVICE INVENTORY STATUS */}
        <div className="hp-dashboard_grid_side">
          <div className="hp-card">
            <div className="hp-card_header">
              <div className="hp-card_header_title">
                <WatchIcon className="hp-card_icon" />
                <div>
                  <h3>Device Inventory Status</h3>
                  <p>Hardware allocation overview</p>
                </div>
              </div>
            </div>

            <div className="hp-card_body">
              <div className="hp-inventory-status">
                <div className="hp-inventory-status_top">
                  <div>
                    <span className="hp-inventory-status_pct">
                      {totalDev > 0 ? Math.round((assigned / totalDev) * 100) : 0}%
                    </span>
                    <span className="hp-inventory-status_label">Allocation Rate</span>
                  </div>
                  <span className="hp-inventory-status_count">
                    {assigned} / {totalDev} Allocated
                  </span>
                </div>

                <div className="hp-inventory-legend">
                  <div className="hp-inventory-legend_item">
                    <strong>{assigned}</strong>
                    <span>Assigned</span>
                  </div>
                  <div className="hp-inventory-legend_item">
                    <strong>{available}</strong>
                    <span>Available</span>
                  </div>
                  <div className="hp-inventory-legend_item">
                    <strong>{totalDev}</strong>
                    <span>Total</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HPDashboard;


